import * as React from 'react';
import { observableValue, derived } from '@vscode/observables';
import { view, viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import type { ExplorerModel, PreviewRow } from './ExplorerModel.js';
import { FixturePreviewItem } from './FixturePreviewItem.js';
import { buildQueryParamUrl } from '../lib/queryParamObservable.js';
import { ScreenshotComparisonRow } from './ScreenshotComparisonRow.js';
import { ReportComparisonRow } from './ReportComparisonRow.js';
import { getAllCachedHeights, setCachedHeight } from '../lib/fixtureSizeCache.js';

const ESTIMATED_ROW_HEIGHT = 280;
const ROW_GAP = 32;
const CONTENT_PADDING = 32;
const OVERSCAN_PX = 400;

// ---------------------------------------------------------------------------
// Scroll debug logging
// ---------------------------------------------------------------------------

const SCROLL_DEBUG = true;
let _scrollLogSeq = 0;

function scrollLog(tag: string, data: Record<string, unknown>): void {
  if (!SCROLL_DEBUG) return;
  const seq = ++_scrollLogSeq;
  const ts = performance.now().toFixed(1);
  console.log(
    `%c[scroll #${seq} @${ts}ms] ${tag}`,
    'color: #3b82f6; font-weight: bold',
    data,
  );
}

function scrollWarn(tag: string, data: Record<string, unknown>): void {
  if (!SCROLL_DEBUG) return;
  const seq = ++_scrollLogSeq;
  const ts = performance.now().toFixed(1);
  console.warn(
    `[scroll #${seq} @${ts}ms] ${tag}`,
    data,
  );
}

const SCROLL_ANCHOR_KEY = 'component-explorer:scrollAnchors';
const MAX_ANCHOR_ENTRIES = 100;

// ---------------------------------------------------------------------------
// Scroll anchor persistence (row index + offset, not absolute scrollTop)
// ---------------------------------------------------------------------------

interface ScrollAnchor {
  readonly rowIndex: number;
  readonly offsetFromViewport: number;
}

function loadScrollAnchors(): Record<string, ScrollAnchor> {
  try {
    const stored = localStorage.getItem(SCROLL_ANCHOR_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return {};
}

function saveScrollAnchor(nodeId: string, anchor: ScrollAnchor): void {
  try {
    const all = loadScrollAnchors();
    all[nodeId] = anchor;
    const entries = Object.entries(all);
    const pruned = entries.length > MAX_ANCHOR_ENTRIES
      ? Object.fromEntries(entries.slice(-MAX_ANCHOR_ENTRIES))
      : all;
    localStorage.setItem(SCROLL_ANCHOR_KEY, JSON.stringify(pruned));
  } catch { /* ignore */ }
}

function getScrollAnchor(nodeId: string): ScrollAnchor | undefined {
  return loadScrollAnchors()[nodeId];
}

// ---------------------------------------------------------------------------
// Layout computation (pure functions over data)
// ---------------------------------------------------------------------------

interface RowLayout {
  readonly top: number;
  readonly height: number;
}

function getRowId(row: PreviewRow): string {
  return row.type === 'single' ? row.fixture.id : row.id;
}

function computeLayout(
  rows: readonly PreviewRow[],
  heightCache: ReadonlyMap<string, number>,
): readonly RowLayout[] {
  const result: RowLayout[] = new Array(rows.length);
  let top = CONTENT_PADDING;
  for (let i = 0; i < rows.length; i++) {
    const height = heightCache.get(getRowId(rows[i])) ?? ESTIMATED_ROW_HEIGHT;
    result[i] = { top, height };
    top += height + ROW_GAP;
  }
  return result;
}

function layoutTotalHeight(layout: readonly RowLayout[]): number {
  if (layout.length === 0) return 0;
  const last = layout[layout.length - 1];
  return last.top + last.height + CONTENT_PADDING;
}

/** Binary search for first row whose bottom edge is >= y. */
function findFirstVisibleRow(layout: readonly RowLayout[], y: number): number {
  let lo = 0;
  let hi = layout.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (layout[mid].top + layout[mid].height < y) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

/** Find visible row range for a given scrollTop + viewportHeight. */
function computeVisibleRange(
  layout: readonly RowLayout[],
  scrollTop: number,
  viewportHeight: number,
): { readonly start: number; readonly end: number } {
  if (layout.length === 0) return { start: 0, end: 0 };
  const start = findFirstVisibleRow(layout, scrollTop - OVERSCAN_PX);
  let end = start;
  const bottom = scrollTop + viewportHeight + OVERSCAN_PX;
  while (end < layout.length && layout[end].top < bottom) {
    end++;
  }
  return { start, end };
}

/** Find the row closest to the viewport top edge. */
function computeAnchor(
  layout: readonly RowLayout[],
  scrollTop: number,
): ScrollAnchor | undefined {
  if (layout.length === 0) return undefined;
  const idx = findFirstVisibleRow(layout, scrollTop);
  const rowIndex = Math.min(idx, layout.length - 1);
  return {
    rowIndex,
    offsetFromViewport: layout[rowIndex].top - scrollTop,
  };
}

/** Compute scrollTop to restore an anchor. */
function scrollTopForAnchor(
  layout: readonly RowLayout[],
  anchor: ScrollAnchor,
): number {
  if (anchor.rowIndex >= layout.length) return 0;
  return layout[anchor.rowIndex].top - anchor.offsetFromViewport;
}

// ---------------------------------------------------------------------------
// Path headings
// ---------------------------------------------------------------------------

function getRowPath(row: PreviewRow): string {
  return row.type === 'single' ? row.fixture.path : row.path;
}

function getPathSegments(path: string): readonly string[] {
  return path.split(' / ').slice(1);
}

interface HeadingInfo {
  readonly depth: number;
  readonly name: string;
  readonly nodeId: string;
}

function computeNewHeadings(
  current: readonly string[],
  previous: readonly string[] | undefined,
  rowId: string,
): readonly HeadingInfo[] {
  const idSegments = rowId.split('/');
  const offset = idSegments.length - current.length;
  for (let i = 0; i < current.length; i++) {
    if (!previous || i >= previous.length || current[i] !== previous[i]) {
      return current.slice(i).map((name, j) => ({
        depth: i + j,
        name,
        nodeId: idSegments.slice(0, offset + i + j + 1).join('/'),
      }));
    }
  }
  return [];
}

const headingTags = ['h1', 'h2', 'h3', 'h4'] as const;

function headingTagForDepth(depth: number): (typeof headingTags)[number] {
  return headingTags[Math.min(depth, headingTags.length - 1)];
}

const headingFontSizes = ['22px', '15px', '13px', '12px'];

function headingStyleForDepth(depth: number): React.CSSProperties {
  return {
    fontSize: headingFontSizes[Math.min(depth, headingFontSizes.length - 1)],
    fontWeight: depth <= 1 ? 600 : 500,
    color: 'var(--component-explorer-foreground)',
    margin: 0,
    marginBottom: depth === 0 ? '8px' : '4px',
    paddingBottom: depth === 0 ? '6px' : undefined,
    borderBottom: depth === 0 ? '1px solid var(--component-explorer-widget-border, var(--component-explorer-panel-border, rgba(128,128,128,0.35)))' : undefined,
  };
}

// ---------------------------------------------------------------------------
// PreviewAreaModel
// ---------------------------------------------------------------------------

class PreviewAreaModel extends ViewModel({
  model: prop.const<ExplorerModel>(),
}) {
  private readonly _rowHeights = observableValue<ReadonlyMap<string, number>>(this, getAllCachedHeights());
  private readonly _scrollTop = observableValue<number>(this, 0);
  private readonly _viewportHeight = observableValue<number>(this, 0);

  private _scrollContainerRef: HTMLDivElement | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _viewportResizeObserver: ResizeObserver | null = null;
  private _currentNodeId: string | undefined = undefined;
  private _scrollSaveTimeout: ReturnType<typeof setTimeout> | null = null;
  private _suppressScrollHandler = false;

  get explorerModel() { return this.props.model; }

  get currentSessionName(): string {
    const daemon = this.explorerModel.daemon;
    if (!daemon) return 'current';
    const sessions = daemon.sessions.get();
    return sessions.find(s => s.sourceKind === 'current')?.name ?? sessions[0]?.name ?? 'current';
  }

  get baselineSessionName(): string {
    const daemon = this.explorerModel.daemon;
    if (!daemon) return 'baseline';
    const sessions = daemon.sessions.get();
    return sessions.find(s => s.sourceKind === 'worktree')?.name ?? sessions[1]?.name ?? 'baseline';
  }

  readonly layout = derived(this, (reader) => {
    const rows = this.explorerModel.selectedFixtures.read(reader);
    const heights = this._rowHeights.read(reader);
    return computeLayout(rows, heights);
  });

  readonly visibleRowRange = derived(this, (reader) => {
    const layout = this.layout.read(reader);
    const scrollTop = this._scrollTop.read(reader);
    const viewportHeight = this._viewportHeight.read(reader);
    return computeVisibleRange(layout, scrollTop, viewportHeight);
  });

  setScrollContainerRef = (el: HTMLDivElement | null): void => {
    if (this._scrollContainerRef) {
      this._scrollContainerRef.removeEventListener('scroll', this._handleScroll);
    }
    this._viewportResizeObserver?.disconnect();

    this._scrollContainerRef = el;
    if (!el) return;

    el.addEventListener('scroll', this._handleScroll, { passive: true });
    this._viewportHeight.set(el.clientHeight, undefined);
    scrollLog('setScrollContainerRef', { clientHeight: el.clientHeight, scrollTop: el.scrollTop });

    this._viewportResizeObserver = new ResizeObserver(([entry]) => {
      const h = Math.round(entry.contentRect.height);
      const prev = this._viewportHeight.get();
      if (h !== prev) {
        scrollLog('viewportResize', { prev, new: h });
      }
      this._viewportHeight.set(h, undefined);
    });
    this._viewportResizeObserver.observe(el);

    this._restoreScrollPosition();
  };

  updateForSelection(nodeId: string | undefined): void {
    if (this._currentNodeId === nodeId) return;
    scrollLog('updateForSelection', { prev: this._currentNodeId, new: nodeId });
    this._currentNodeId = nodeId;
    this._restoreScrollPosition();
  }

  private _restoreScrollPosition(): void {
    if (!this._scrollContainerRef || !this._currentNodeId) return;
    const anchor = getScrollAnchor(this._currentNodeId);
    if (!anchor) {
      scrollLog('restoreScroll:noAnchor', { nodeId: this._currentNodeId });
      return;
    }

    const layout = this.layout.get();
    const target = scrollTopForAnchor(layout, anchor);
    const prevScrollTop = this._scrollContainerRef.scrollTop;

    scrollLog('restoreScroll', {
      nodeId: this._currentNodeId,
      anchor,
      prevScrollTop,
      targetScrollTop: target,
      delta: target - prevScrollTop,
      layoutLength: layout.length,
    });

    this._suppressScrollHandler = true;
    this._scrollContainerRef.scrollTop = target;
    this._scrollTop.set(target, undefined);
    requestAnimationFrame(() => { this._suppressScrollHandler = false; });
  }

  private _handleScroll = (): void => {
    if (!this._scrollContainerRef) return;
    const prevObsScrollTop = this._scrollTop.get();
    const newScrollTop = this._scrollContainerRef.scrollTop;
    const delta = newScrollTop - prevObsScrollTop;
    this._scrollTop.set(newScrollTop, undefined);

    if (Math.abs(delta) > 200) {
      scrollWarn('handleScroll:LARGE_JUMP', {
        prevObsScrollTop,
        newScrollTop,
        delta,
        suppressed: this._suppressScrollHandler,
        stack: new Error().stack,
      });
    }

    if (this._suppressScrollHandler || !this._currentNodeId) {
      if (this._suppressScrollHandler) {
        scrollLog('handleScroll:suppressed', { newScrollTop, delta });
      }
      return;
    }
    if (this._scrollSaveTimeout) clearTimeout(this._scrollSaveTimeout);
    this._scrollSaveTimeout = setTimeout(() => {
      if (!this._currentNodeId) return;
      const layout = this.layout.get();
      const anchor = computeAnchor(layout, this._scrollTop.get());
      if (anchor) saveScrollAnchor(this._currentNodeId, anchor);
    }, 150);
  };

  observeRowElement = (el: HTMLDivElement | null, id: string): void => {
    if (!el) return;
    if (!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver((entries) => {
        const current = this._rowHeights.get();
        let newMap: Map<string, number> | undefined;
        const changedRows: { rid: string; oldH: number | undefined; newH: number }[] = [];
        for (const entry of entries) {
          const rid = entry.target.getAttribute('data-row-id');
          if (!rid) continue;
          const height = Math.round(entry.contentRect.height);
          if (height > 0 && current.get(rid) !== height) {
            changedRows.push({ rid, oldH: current.get(rid), newH: height });
            if (!newMap) newMap = new Map(current);
            newMap.set(rid, height);
            setCachedHeight(rid, height);
          }
        }
        if (newMap) {
          const oldLayout = this.layout.get();
          const scrollTopBefore = this._scrollTop.get();
          const anchor = computeAnchor(oldLayout, scrollTopBefore);

          this._rowHeights.set(newMap, undefined);

          const newLayout = this.layout.get();

          if (anchor && this._scrollContainerRef) {
            const newScrollTop = scrollTopForAnchor(newLayout, anchor);
            const delta = newScrollTop - scrollTopBefore;

            if (Math.abs(delta) > 2) {
              scrollLog('resizeObserver:reAnchor', {
                changedRows,
                anchor,
                scrollTopBefore,
                newScrollTop,
                delta,
                oldTotalHeight: layoutTotalHeight(oldLayout),
                newTotalHeight: layoutTotalHeight(newLayout),
              });
            }
            if (Math.abs(delta) > 200) {
              scrollWarn('resizeObserver:LARGE_REANCHOR', {
                changedRows,
                anchor,
                scrollTopBefore,
                newScrollTop,
                delta,
              });
            }

            this._suppressScrollHandler = true;
            this._scrollContainerRef.scrollTop = newScrollTop;
            this._scrollTop.set(newScrollTop, undefined);
            requestAnimationFrame(() => { this._suppressScrollHandler = false; });
          }
        }
      });
    }
    el.setAttribute('data-row-id', id);
    this._resizeObserver.observe(el);
  };

  dispose(): void {
    this._resizeObserver?.disconnect();
    this._viewportResizeObserver?.disconnect();
    if (this._scrollContainerRef) {
      this._scrollContainerRef.removeEventListener('scroll', this._handleScroll);
    }
    if (this._scrollSaveTimeout) clearTimeout(this._scrollSaveTimeout);
  }
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export const PreviewArea = viewWithModel(
  PreviewAreaModel,
  {},
  (reader, model) => {
    const rows = model.explorerModel.selectedFixtures.read(reader);
    const selectedNodeId = model.explorerModel.selectedNodeId.read(reader);
    const layout = model.layout.read(reader);
    const { start, end } = model.visibleRowRange.read(reader);

    model.updateForSelection(selectedNodeId);

    if (rows.length === 0) {
      const reportModel = model.explorerModel.reportModel;
      if (reportModel) {
        const reportState = reportModel.state.read(reader);
        if (reportState.status === 'loading') {
          return (
            <div style={styles.emptyState}>
              <span style={styles.emptyText}>Loading report...</span>
            </div>
          );
        }
        if (reportState.status === 'error') {
          return (
            <div style={styles.emptyState}>
              <span style={{ ...styles.emptyText, color: 'var(--component-explorer-errorForeground)' }}>
                Failed to load report: {reportState.message}
              </span>
            </div>
          );
        }
      }
      return (
        <div style={styles.emptyState}>
          <span style={styles.emptyText}>Select a component or folder to preview</span>
        </div>
      );
    }

    const total = layoutTotalHeight(layout);

    return (
      <div ref={model.setScrollContainerRef} style={styles.scrollContainer}>
        <div style={{ position: 'relative', height: total }}>
          {rows.slice(start, end).map((row, i) => {
            const idx = start + i;
            const { top } = layout[idx];
            const id = getRowId(row);

            const currentSegments = getPathSegments(getRowPath(row));
            const prevSegments = idx > 0 ? getPathSegments(getRowPath(rows[idx - 1])) : undefined;
            const newHeadings = computeNewHeadings(currentSegments, prevSegments, id);

            return (
              <div
                key={id}
                ref={(el) => model.observeRowElement(el, id)}
                style={{ position: 'absolute', top, left: CONTENT_PADDING, right: CONTENT_PADDING }}
              >
                {newHeadings.map((h) => {
                  const Tag = headingTagForDepth(h.depth);
                  return (
                    <Tag key={h.depth} style={headingStyleForDepth(h.depth)}>
                      <a
                        href={buildQueryParamUrl('fixture', h.nodeId)}
                        style={styles.headingLink}
                        onClick={(e) => { e.preventDefault(); model.explorerModel.selectNode(h.nodeId); }}
                      >{h.name}</a>
                    </Tag>
                  );
                })}
                {row.type === 'single' ? (
                  <FixturePreviewItem fixture={row.fixture} compact={false} onSelect={model.explorerModel.selectNode} />
                ) : row.type === 'variants' ? (
                  <VariantsRowView row={row} onSelect={model.explorerModel.selectNode} />
                ) : row.type === 'screenshot-comparison' && model.explorerModel.daemon ? (
                  <ScreenshotComparisonRow
                    name={row.name}
                    path={row.path}
                    items={row.items}
                    daemon={model.explorerModel.daemon}
                    baselineSession={model.baselineSessionName}
                    currentSession={model.currentSessionName}
                  />
                ) : row.type === 'report-comparison' && model.explorerModel.reportModel ? (
                  <ReportComparisonRow
                    name={row.name}
                    path={row.path}
                    items={row.items}
                    reportModel={model.explorerModel.reportModel}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

type VariantsRowData = Extract<PreviewRow, { type: 'variants' }>;

const VariantsRowView = view(
  {
    row: prop.const<VariantsRowData>(),
    onSelect: prop.const<(nodeId: string) => void>(),
  },
  (_reader, props) => {
    const row = props.row;
    return (
      <div style={styles.variantsContent}>
        {row.fixtures.map((fixture) => (
          <FixturePreviewItem key={fixture.id} fixture={fixture} compact onSelect={props.onSelect} />
        ))}
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = {
  scrollContainer: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: 'var(--component-explorer-editor-background)',
  } as React.CSSProperties,
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--component-explorer-editor-background)',
  } as React.CSSProperties,
  emptyText: {
    color: 'var(--component-explorer-descriptionForeground)',
    fontSize: '14px',
  } as React.CSSProperties,
  variantsHeader: {
    marginBottom: '12px',
  } as React.CSSProperties,
  variantsTitle: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--component-explorer-foreground)',
  } as React.CSSProperties,
  variantsContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '16px',
  } as React.CSSProperties,
  headingLink: {
    textDecoration: 'none',
    color: 'inherit',
  } as React.CSSProperties,
};
